import * as THREE from 'three';
import type { OcctMesh, OcctNode, OcctResult } from './types';

const DEFAULT_COLOR = 0x8fa3b8;

function toFloat32(array: ArrayLike<number>): Float32Array {
  return array instanceof Float32Array ? array : new Float32Array(array);
}

function toUint32(array: ArrayLike<number>): Uint32Array {
  return array instanceof Uint32Array ? array : Uint32Array.from(array as ArrayLike<number>);
}

function colorOf(raw: number[] | null | undefined, fallback: THREE.Color): THREE.Color {
  return raw && raw.length === 3 ? new THREE.Color(raw[0], raw[1], raw[2]) : fallback;
}

/**
 * A STEP file typically paints hundreds of faces with a handful of distinct
 * colors; one MeshStandardMaterial per color instead of per face keeps the
 * shader/program count (and draw-call state changes) down.
 */
class MaterialCache {
  private readonly byKey = new Map<string, THREE.MeshStandardMaterial>();

  get(color: THREE.Color): THREE.MeshStandardMaterial {
    const key = color.getHexString();
    let material = this.byKey.get(key);
    if (!material) {
      material = new THREE.MeshStandardMaterial({
        color: color.clone(),
        metalness: 0.1,
        roughness: 0.6,
        side: THREE.DoubleSide
      });
      this.byKey.set(key, material);
    }
    return material;
  }

  get size(): number {
    return this.byKey.size;
  }
}

function buildMesh(meshData: OcctMesh, cache: MaterialCache, meshIndex: number): THREE.Mesh {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(toFloat32(meshData.attributes.position.array), 3));
  if (meshData.attributes.normal) {
    geometry.setAttribute('normal', new THREE.BufferAttribute(toFloat32(meshData.attributes.normal.array), 3));
  } else {
    geometry.computeVertexNormals();
  }
  const index = meshData.index && meshData.index.array ? toUint32(meshData.index.array) : null;
  if (index) geometry.setIndex(new THREE.BufferAttribute(index, 1));

  const defaultColor = colorOf(meshData.color, new THREE.Color(DEFAULT_COLOR));
  const materials: THREE.Material[] = [cache.get(defaultColor)];

  // Each STEP face can carry its own color (e.g. a red gasket face on an
  // otherwise grey part) -- occt-import-js reports these as ranges in
  // brep_faces, so build one geometry group + material per colored range,
  // same approach as occt-import-js's own three_viewer example.
  const brepFaces = meshData.brep_faces;
  if (index && brepFaces && brepFaces.length > 0) {
    brepFaces.forEach(f => materials.push(cache.get(colorOf(f.color, defaultColor))));

    const triangleCount = index.length / 3;
    let triIndex = 0;
    let faceGroupIndex = 0;
    while (triIndex < triangleCount) {
      const first = triIndex;
      let last: number;
      let materialIndex: number;
      if (faceGroupIndex >= brepFaces.length) {
        last = triangleCount; materialIndex = 0;
      } else if (triIndex < brepFaces[faceGroupIndex].first) {
        last = brepFaces[faceGroupIndex].first; materialIndex = 0;
      } else {
        last = brepFaces[faceGroupIndex].last + 1;
        materialIndex = faceGroupIndex + 1;
        faceGroupIndex++;
      }
      geometry.addGroup(first * 3, (last - first) * 3, materialIndex);
      triIndex = last;
    }
  }

  const mesh = new THREE.Mesh(geometry, materials.length > 1 ? materials : materials[0]);
  mesh.name = (meshData.name && meshData.name.trim()) || `Body ${meshIndex + 1}`;
  mesh.userData.brepFaces = brepFaces || null;
  mesh.userData.meshIndex = meshIndex;
  mesh.userData.isModelBody = true;
  return mesh;
}

export interface BuildStats {
  meshCount: number;
  materialCount: number;
  nodeCount: number;
}

/** UI / scene tree node mirroring the STEP product structure. */
export interface ModelTreeNode {
  id: string;
  name: string;
  object: THREE.Object3D;
  children: ModelTreeNode[];
  /** True when this node is a tessellated body (leaf mesh). */
  isBody: boolean;
}

function displayName(raw: string | undefined, fallback: string): string {
  const t = raw?.trim();
  return t ? t : fallback;
}

function attachMesh(
  parent: THREE.Object3D,
  meshPool: Array<THREE.Mesh | null>,
  meshIndex: number
): THREE.Mesh | null {
  if (meshIndex < 0 || meshIndex >= meshPool.length) return null;
  const existing = meshPool[meshIndex];
  if (!existing) return null;
  // A mesh can only have one parent; clone if the STEP tree references it again.
  if (existing.parent) {
    const clone = existing.clone(false);
    clone.geometry = existing.geometry;
    clone.material = existing.material;
    clone.userData = { ...existing.userData };
    parent.add(clone);
    return clone;
  }
  parent.add(existing);
  return existing;
}

function buildHierarchy(
  node: OcctNode,
  meshPool: Array<THREE.Mesh | null>,
  id: string,
  depth: number
): { group: THREE.Group; tree: ModelTreeNode } {
  const group = new THREE.Group();
  group.name = displayName(node.name, depth === 0 ? 'Model' : 'Assembly');
  group.userData.isModelNode = true;

  const treeChildren: ModelTreeNode[] = [];

  for (const meshIndex of node.meshes || []) {
    const mesh = attachMesh(group, meshPool, meshIndex);
    if (!mesh) continue;
    treeChildren.push({
      id: `${id}/m${meshIndex}-${treeChildren.length}`,
      name: mesh.name,
      object: mesh,
      children: [],
      isBody: true
    });
  }

  (node.children || []).forEach((child, i) => {
    const built = buildHierarchy(child, meshPool, `${id}/${i}`, depth + 1);
    group.add(built.group);
    treeChildren.push(built.tree);
  });

  return {
    group,
    tree: {
      id,
      name: group.name,
      object: group,
      children: treeChildren,
      isBody: false
    }
  };
}

function flattenFallback(meshes: THREE.Mesh[], fileLabel: string): { group: THREE.Group; tree: ModelTreeNode } {
  const group = new THREE.Group();
  group.name = fileLabel;
  const children: ModelTreeNode[] = meshes.map((mesh, i) => {
    group.add(mesh);
    return {
      id: `flat/${i}`,
      name: mesh.name,
      object: mesh,
      children: [],
      isBody: true
    };
  });
  return {
    group,
    tree: { id: 'root', name: fileLabel, object: group, children, isBody: false }
  };
}

function countNodes(node: ModelTreeNode): number {
  return 1 + node.children.reduce((n, c) => n + countNodes(c), 0);
}

export function buildModel(result: OcctResult, fileLabel = 'Model'): {
  group: THREE.Group;
  stats: BuildStats;
  tree: ModelTreeNode;
} {
  const cache = new MaterialCache();
  const meshPool: Array<THREE.Mesh | null> = result.meshes.map((meshData, i) =>
    buildMesh(meshData, cache, i)
  );

  let group: THREE.Group;
  let tree: ModelTreeNode;

  if (result.root) {
    const built = buildHierarchy(result.root, meshPool, 'root', 0);
    group = built.group;
    tree = built.tree;
    // Prefer the file name on an empty-named root.
    if (!result.root.name?.trim()) {
      tree.name = fileLabel;
      group.name = fileLabel;
    }
    // Orphan meshes not referenced by the tree (defensive).
    meshPool.forEach((mesh, i) => {
      if (mesh && !mesh.parent) {
        group.add(mesh);
        tree.children.push({
          id: `orphan/${i}`,
          name: mesh.name,
          object: mesh,
          children: [],
          isBody: true
        });
      }
    });
  } else {
    const meshes = meshPool.filter((m): m is THREE.Mesh => !!m);
    const built = flattenFallback(meshes, fileLabel);
    group = built.group;
    tree = built.tree;
  }

  return {
    group,
    tree,
    stats: {
      meshCount: result.meshes.length,
      materialCount: cache.size,
      nodeCount: countNodes(tree)
    }
  };
}
